/**
 * Saved listings ("favourites").
 *
 * The Sharetribe Web Template has no favourites feature, and Console has no
 * config for one, so this is entirely code-side. Ids are stored on the current
 * user's own profile `privateData`, which only that user can read — a saved
 * list is nobody else's business, and privateData is the only profile scope
 * with that property.
 *
 * These helpers are pure so the toggle logic stays testable without a store.
 */

/**
 * The listing ids the given user has saved.
 *
 * @param {Object} currentUser the currentUser entity, may be null
 * @returns {Array<string>} listing uuids, never null
 */
export const getFavoriteIds = currentUser => {
  const favorites = currentUser?.attributes?.profile?.privateData?.favorites;
  return Array.isArray(favorites) ? favorites : [];
};

/**
 * Whether a listing is saved by the given user.
 *
 * @param {Object} currentUser the currentUser entity, may be null
 * @param {string} listingId listing uuid
 * @returns {boolean}
 */
export const isFavorite = (currentUser, listingId) =>
  !!listingId && getFavoriteIds(currentUser).includes(listingId);

/**
 * The list with the given id added, or removed if it was already there.
 *
 * @param {Array<string>} favorites current ids
 * @param {string} listingId listing uuid
 * @returns {Array<string>} the next list
 */
export const toggleFavoriteId = (favorites, listingId) => {
  const current = Array.isArray(favorites) ? favorites : [];
  return current.includes(listingId)
    ? current.filter(id => id !== listingId)
    : [...current, listingId];
};
